import { Icon } from 'components';
import { Api } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  Avatar32,
  PopoverEmpty,
  PopoverList,
  PopoverRow,
  PopoverSearchInput,
  PopoverSearchRow,
  RowMeta,
  RowName
} from '../../../../components';
import modernist from '../../../../modernistTheme';
import ShoutoutDetails from '../ShoutoutDetails';

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$split ? '1fr 1.1fr' : '1fr')};
`;

const LeftCol = styled.div`
  padding: 28px 32px 32px 32px;
`;

const RightCol = styled.div`
  border-left: 2px solid ${modernist.text};
`;

const KindsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-top: 20px;
  border: 2px solid ${modernist.text};
`;

const KindBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 14px 16px;
  border: 0;
  border-left: ${(props) => (props.$divider ? `2px solid ${modernist.text}` : 0)};
  background: ${(props) => (props.$active ? modernist.accent : 'transparent')};
  color: ${(props) => (props.$active ? '#fff' : modernist.neutral700)};
  text-align: left;
  font-family: ${modernist.fontBody};
  opacity: ${(props) => (props.disabled ? 0.45 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const KindLabel = styled.span`
  font-family: ${modernist.fontHeading};
  font-weight: 800;
  font-size: 15px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

let timer;

const Shoutout = ({ data, setData, handleClose }) => {
  const [query, setQuery] = useState(''),
    [results, setResults] = useState([]),
    { t } = useTranslation();

  const hasTarget = !!data.entity?._id || !!data.eventObject?._id;

  useEffect(() => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      const res = await Api.post('/users/search', { text: query, type: 'team' });
      setResults(res || []);
    }, 300);
  }, [query]);

  function pick(user) {
    setData((prev) => ({ ...prev, entity: user }));
  }

  return (
    <Grid $split={hasTarget}>
      <LeftCol>
        <KindsGrid>
          <KindBtn type="button" $active>
            <Icon type="throphy" dimensions={20} col="#fff" />
            <KindLabel>{t('common.team')}</KindLabel>
          </KindBtn>
          <KindBtn type="button" $divider disabled>
            <Icon type="athletes" dimensions={20} col={modernist.neutral700} />
            <KindLabel>{t('common.athlete')}</KindLabel>
          </KindBtn>
        </KindsGrid>

        <PopoverSearchRow
          style={{ marginTop: 20, borderTop: 0, borderBottom: `1px solid ${modernist.neutral400}` }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="square"
            style={{ flex: 'none', color: modernist.neutral600 }}
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <PopoverSearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('add.searchTeams', 'Search teams')}
          />
        </PopoverSearchRow>

        <PopoverList style={{ maxHeight: 268, marginTop: 4 }}>
          {results.map((r) => {
            const isSelected = data.entity?._id === r._id;
            return (
              <PopoverRow
                key={r._id}
                type="button"
                $selected={isSelected}
                onClick={() => pick(r)}
                style={{ gridTemplateColumns: '36px 1fr 20px' }}
              >
                <Avatar32 $selected={isSelected} $src={r.avatar}>
                  {!r.avatar &&
                    (r.name || '')
                      .split(' ')
                      .map((p) => p[0])
                      .join('')
                      .slice(0, 2)}
                </Avatar32>
                <span style={{ minWidth: 0 }}>
                  <RowName>{r.name}</RowName>
                  <RowMeta>@{r.username}</RowMeta>
                </span>
                {isSelected && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3.5"
                    strokeLinecap="square"
                    style={{ color: modernist.accent }}
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </PopoverRow>
            );
          })}

          {!results.length && <PopoverEmpty>{t('add.noUserFound')}</PopoverEmpty>}
        </PopoverList>
      </LeftCol>

      {hasTarget && (
        <RightCol>
          <ShoutoutDetails data={data} setData={setData} handleClose={handleClose} />
        </RightCol>
      )}
    </Grid>
  );
};

export default Shoutout;
